import TabPanel from '@components/Tabs/TabPanel';
import { Box, SxProps, Tab, Tabs } from '@mui/material';
import { a11yProps } from '@utils/a11y';
import { useLocationNoUpdates, useNavigate } from 'hooks';
import React, { FC } from 'react';

interface ITab {
	id: string;
	component: React.ReactElement;
}

interface ITabsPageProps {
	tabs: ITab[];
	routeName: string;
	fullRoute: string;
	containerSx?: SxProps;
	tabsSx?: SxProps;
	tabPanelSx?: SxProps;
	callback?: (id: string) => void;
	selectedTabId?: string;
	borderBottom?: boolean;
}

const HorizontalTabsPage: FC<ITabsPageProps> = ({
	tabs,
	routeName,
	fullRoute,
	containerSx,
	tabsSx,
	tabPanelSx,
	callback,
	selectedTabId,
	borderBottom
}) => {
	const { pathname } = useLocationNoUpdates();

	const navigate = useNavigate();

	const currentTab = Math.max(
		tabs.findIndex((tab) =>
			selectedTabId
				? selectedTabId === tab.id
				: pathname.endsWith(tab.id.toLowerCase())
		),
		0
	);
	const page = tabs[currentTab].id;

	const tabsId = 'dual-tabs-horizontal-tab';
	const tabsPanelId = 'dual-tabs-horizontal-tab-panel';
	return (
		<Box display="flex" flexGrow={1} flexDirection="column" sx={containerSx}>
			<Box
				borderBottom={borderBottom ? 1 : undefined}
				borderColor="divider"
				sx={tabsSx}
			>
				<Tabs
					orientation="horizontal"
					value={currentTab}
					onChange={(_, value: number) => {
						if (tabs[value]) {
							if (callback) {
								callback(tabs[value].id);
							} else {
								navigate(`${fullRoute}/${tabs[value].id.toLowerCase()}`);
							}
						}
					}}
					aria-label={intl.tr(`${routeName}.tabs.${page}.title`)}
				>
					{tabs.map((tab) => (
						<Tab
							key={tab.id}
							data-testid={`${tabsId}-${tab.id}`}
							label={
								<Box alignContent="center">
									{intl.tr(`${routeName}.tabs.${tab.id}.title`)}
								</Box>
							}
							{...a11yProps(tabsId, tabsPanelId, tab.id)}
						/>
					))}
				</Tabs>
			</Box>
			<Box pt={3} display="flex" flexGrow={1} sx={tabPanelSx}>
				{tabs.map((tab) => (
					<TabPanel
						value={page}
						key={tab.id}
						id={tab.id}
						labelId={tabsPanelId}
						controllerId={tabsId}
					>
						{tab.component}
					</TabPanel>
				))}
			</Box>
		</Box>
	);
};

HorizontalTabsPage.defaultProps = {
	containerSx: {},
	tabsSx: {},
	tabPanelSx: {},
	callback: undefined,
	selectedTabId: undefined,
	borderBottom: true
};

export default HorizontalTabsPage;
